import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Card, Button, Select } from '../../components/ui/Kit';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { bootCampChapters, bootCampVideos, bootCampVideoCount } from '../../data/bootCampVideos';
import { api, supabase } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { roleOptionLabel } from '../../lib/roleOptions';
import { getInitials } from '../../lib/calendarUtils';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';

const { FiVideo, FiPlayCircle, FiCheckCircle, FiCircle, FiRotateCcw, FiMessageSquare, FiPlus } = FiIcons;

const PROGRESS_KEY = 'prosperity_bootcamp_progress';
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '🙏', '🔥'];

function videoSlugFromUrl(url) {
  const filename = url.split('/').pop() || '';
  return filename.replace(/\.[^.]+$/, '');
}

function roleBadgeClass(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'admin' || r === 'super_admin') return 'bg-rose-100 text-rose-800';
  if (r === 'manager') return 'bg-purple-100 text-purple-800';
  if (r === 'closer') return 'bg-blue-100 text-blue-800';
  if (r === 'setter') return 'bg-green-100 text-green-800';
  if (r === 'associate') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-800';
}

function avatarBgForRole(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'admin' || r === 'super_admin') return 'bg-purple-600';
  if (r === 'manager') return 'bg-[#00AAFF]';
  if (r === 'setter' || r === 'associate') return 'bg-amber-500';
  if (r === 'closer') return 'bg-green-600';
  return 'bg-gray-500';
}

function extractMentionedUserIds(body, users) {
  const ids = [];
  for (const u of users) {
    const name = u.full_name?.trim();
    if (!name) continue;
    if (body.includes(`@${name}`)) ids.push(u.id);
  }
  return [...new Set(ids)];
}

function renderCommentBody(body, agencyUsers = []) {
  if (!body) return null;
  const names = agencyUsers
    .map((u) => u.full_name?.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  let remaining = body;
  const segments = [];
  while (remaining.length) {
    let matched = false;
    for (const name of names) {
      const token = `@${name}`;
      const idx = remaining.indexOf(token);
      if (idx !== -1) {
        if (idx > 0) segments.push({ type: 'text', value: remaining.slice(0, idx) });
        segments.push({ type: 'mention', value: token });
        remaining = remaining.slice(idx + token.length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      segments.push({ type: 'text', value: remaining });
      break;
    }
  }

  return segments.map((s, i) =>
    s.type === 'mention' ? (
      <span key={i} className="text-[#00AAFF] font-medium">
        {s.value}
      </span>
    ) : (
      s.value
    ),
  );
}

function groupReactions(reactions, userId) {
  const map = new Map();
  for (const r of reactions || []) {
    if (!map.has(r.emoji)) {
      map.set(r.emoji, { emoji: r.emoji, count: 0, userReacted: false, ids: [] });
    }
    const entry = map.get(r.emoji);
    entry.count += 1;
    entry.ids.push(r.id);
    if (r.user_id === userId) entry.userReacted = true;
  }
  return [...map.values()];
}

function updateCommentTree(comments, commentId, updater) {
  return comments.map((c) => {
    if (c.id === commentId) return updater(c);
    if (c.replies?.length) {
      const replies = c.replies.map((r) => (r.id === commentId ? updater(r) : r));
      if (replies !== c.replies) return { ...c, replies };
    }
    return c;
  });
}

function removeCommentFromTree(comments, commentId) {
  return comments
    .filter((c) => c.id !== commentId)
    .map((c) => ({
      ...c,
      replies: (c.replies || []).filter((r) => r.id !== commentId),
    }));
}

function appendRealtimeComment(prev, row, agencyUsers) {
  if (row.deleted_at) return prev;
  const userObj = agencyUsers.find((u) => u.id === row.user_id);
  const enriched = {
    ...row,
    user: userObj
      ? {
          id: userObj.id,
          full_name: resolveDisplayName(row.user_id, userObj) || userObj.full_name,
          role: userObj.role,
        }
      : row.user ?? null,
    reactions: [],
  };

  if (row.parent_id) {
    return prev.map((c) => {
      if (c.id !== row.parent_id) return c;
      if ((c.replies || []).some((r) => r.id === row.id)) return c;
      return { ...c, replies: [...(c.replies || []), enriched] };
    });
  }

  if (prev.some((c) => c.id === row.id)) return prev;
  return [...prev, { ...enriched, replies: [] }];
}

function loadProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function MentionTextarea({
  value,
  onChange,
  onSubmit,
  placeholder,
  agencyUsers,
  disabled,
  submitLabel = 'Post',
  compact = false,
  composeMode = false,
  videoTitle,
}) {
  const textareaRef = useRef(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(null);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const filteredUsers = useMemo(() => {
    const q = mentionQuery.toLowerCase();
    return agencyUsers
      .filter((u) => u.full_name?.trim())
      .filter((u) => !q || u.full_name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [agencyUsers, mentionQuery]);

  const detectMention = useCallback((text, cursor) => {
    const before = text.slice(0, cursor);
    const atIndex = before.lastIndexOf('@');
    if (atIndex === -1) {
      setMentionOpen(false);
      return;
    }
    const afterAt = before.slice(atIndex + 1);
    if (afterAt.includes('\n') || afterAt.includes('  ')) {
      setMentionOpen(false);
      return;
    }
    setMentionStart(atIndex);
    setMentionQuery(afterAt);
    setMentionOpen(true);
    setHighlightIdx(0);
  }, []);

  const insertMention = useCallback(
    (selectedUser) => {
      const name = selectedUser.full_name.trim();
      const ta = textareaRef.current;
      if (!ta || mentionStart == null) return;
      const before = value.slice(0, mentionStart);
      const after = value.slice(ta.selectionStart);
      const insertion = `@${name} `;
      const next = before + insertion + after;
      onChange(next);
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      requestAnimationFrame(() => {
        const pos = before.length + insertion.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      });
    },
    [mentionStart, onChange, value],
  );

  const handleChange = (e) => {
    onChange(e.target.value);
    detectMention(e.target.value, e.target.selectionStart);
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && filteredUsers.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx((i) => (i + 1) % filteredUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx((i) => (i - 1 + filteredUsers.length) % filteredUsers.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertMention(filteredUsers[highlightIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <div className="relative">
      {composeMode && videoTitle && (
        <span className="inline-flex items-center rounded-full bg-[#00AAFF]/10 px-2.5 py-1 text-xs font-semibold text-[#00AAFF] mb-2">
          Commenting on: {videoTitle}
        </span>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => detectMention(value, e.target.selectionStart)}
        placeholder={placeholder}
        disabled={disabled}
        rows={compact ? 2 : 3}
        className={`w-full rounded-md text-sm text-gray-900 placeholder-gray-400 focus:border-[#00AAFF] focus:ring-2 focus:ring-[#00AAFF]/30 outline-none resize-none ${
          composeMode
            ? 'border-2 border-gray-200 bg-white px-4 py-3 min-h-[100px]'
            : compact
              ? 'border border-gray-200 bg-gray-50 px-3 py-2 min-h-[72px]'
              : 'border border-gray-200 bg-gray-50 px-3 py-2 min-h-[88px]'
        }`}
      />
      {mentionOpen && filteredUsers.length > 0 && (
        <div className="absolute z-30 left-0 right-0 bottom-full mb-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1">
          {filteredUsers.map((u, idx) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(u);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                idx === highlightIdx ? 'bg-blue-50 text-[#00AAFF]' : 'text-gray-800'
              }`}
            >
              {u.full_name}
            </button>
          ))}
        </div>
      )}
      <div className={`flex mt-3 ${composeMode || compact ? 'justify-end' : 'items-center justify-between gap-2'}`}>
        {!compact && !composeMode && <span className="text-xs text-gray-400">Ctrl+Enter to post</span>}
        <Button type="button" size="sm" onClick={onSubmit} disabled={disabled || !value.trim()}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function ReactionBar({ reactions, userId, onToggle, showPicker, onTogglePicker }) {
  const grouped = groupReactions(reactions, userId);

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {grouped.map(({ emoji, count, userReacted }) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onToggle(emoji)}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm border transition-colors ${
            userReacted
              ? 'border-[#00AAFF] bg-blue-50 text-[#00AAFF]'
              : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-[#00AAFF]/50'
          }`}
        >
          <span className="text-base leading-none">{emoji}</span>
          <span className="font-semibold text-sm">{count}</span>
        </button>
      ))}
      <div className="relative">
        <button
          type="button"
          onClick={onTogglePicker}
          className="inline-flex items-center justify-center rounded-full px-3 py-1 min-h-[34px] border border-gray-200 bg-gray-50 text-gray-500 hover:border-[#00AAFF] hover:text-[#00AAFF] transition-colors"
          aria-label="Add reaction"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
        </button>
        {showPicker && (
          <div className="absolute z-20 left-0 top-full mt-1 flex gap-1 rounded-lg border border-gray-200 bg-white shadow-lg p-1.5">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onToggle(emoji);
                  onTogglePicker();
                }}
                className="w-8 h-8 rounded-md hover:bg-gray-100 text-lg leading-none"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentCard({
  comment,
  user,
  activeVideoSlug,
  videoTitleFromSlug,
  onLoadVideo,
  onReply,
  onDelete,
  onToggleReaction,
  isReply = false,
  replyingToId,
  onCancelReply,
  onSubmitReply,
  agencyUsers,
  replyDraft,
  onReplyDraftChange,
  submitting,
  expandedReplies,
  onToggleReplies,
}) {
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const isExpanded = expandedReplies?.has(comment.id);
  const isOwn = comment.user_id === user?.id;
  const displayName = resolveDisplayName(comment.user_id, comment.user) || 'Unknown';
  const showVideoTag = !isReply && activeVideoSlug && comment.video_id !== activeVideoSlug;

  const handleToggleReaction = (emoji) => {
    onToggleReaction(comment.id, emoji);
  };

  return (
    <div className={isReply ? 'ml-10 mt-3' : ''}>
      <div className="flex gap-3">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${avatarBgForRole(
            comment.user?.role,
          )}`}
        >
          {getInitials(displayName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-bold text-gray-900">{displayName}</span>
            {comment.user?.role && (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${roleBadgeClass(
                  comment.user.role,
                )}`}
              >
                {roleOptionLabel(comment.user.role)}
              </span>
            )}
            <span className="text-xs text-gray-400">
              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
            </span>
          </div>

          {showVideoTag && (
            <button
              type="button"
              onClick={() => onLoadVideo(comment.video_id)}
              className="mt-1 inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600 hover:bg-blue-50 hover:text-[#00AAFF] transition-colors"
            >
              📹 {videoTitleFromSlug(comment.video_id)}
            </button>
          )}

          <p className="mt-1.5 text-sm text-gray-800 whitespace-pre-wrap break-words">
            {renderCommentBody(comment.body, agencyUsers)}
          </p>

          <ReactionBar
            reactions={comment.reactions}
            userId={user?.id}
            onToggle={handleToggleReaction}
            showPicker={emojiPickerOpen}
            onTogglePicker={() => setEmojiPickerOpen((o) => !o)}
          />

          {!isReply && (
            <div className="flex flex-wrap items-center gap-4 mt-3">
              {(comment.replies?.length ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => onToggleReplies?.(comment.id)}
                  className="text-sm text-[#00AAFF] hover:underline"
                >
                  ↩ {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                </button>
              )}
              <button
                type="button"
                onClick={() => onReply(comment.id)}
                className="text-sm text-gray-400 hover:text-gray-900 transition-colors"
              >
                Reply
              </button>
              {isOwn && (
                <button
                  type="button"
                  onClick={() => onDelete(comment.id)}
                  className="text-sm text-gray-400 hover:text-gray-900 transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
          )}

          {isReply && isOwn && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="mt-2 text-sm text-gray-400 hover:text-gray-900 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {!isReply && replyingToId === comment.id && (
        <div className="ml-12 mt-3">
          <MentionTextarea
            value={replyDraft}
            onChange={onReplyDraftChange}
            onSubmit={() => onSubmitReply(comment)}
            placeholder="Write a reply... use @ to mention someone"
            agencyUsers={agencyUsers}
            disabled={submitting}
            submitLabel="Reply"
            compact
          />
          <button
            type="button"
            onClick={onCancelReply}
            className="mt-1 text-xs text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      )}

      {!isReply && isExpanded && (comment.replies?.length ?? 0) > 0 && (
        <div className="mt-1">
          {comment.replies.map((reply) => (
            <CommentCard
              key={reply.id}
              comment={reply}
              user={user}
              activeVideoSlug={activeVideoSlug}
              videoTitleFromSlug={videoTitleFromSlug}
              onLoadVideo={onLoadVideo}
              onReply={onReply}
              onDelete={onDelete}
              onToggleReaction={onToggleReaction}
              isReply
              agencyUsers={agencyUsers}
              expandedReplies={expandedReplies}
              onToggleReplies={onToggleReplies}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BootCampDiscussion({
  activeVideo,
  activeVideoSlug,
  videoBySlug,
  onLoadVideo,
}) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [videoFilter, setVideoFilter] = useState('all');
  const { users: directoryUsers } = useUsersDirectory();
  const agencyUsers = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);
  const [composeBody, setComposeBody] = useState('');
  const [replyDraft, setReplyDraft] = useState('');
  const [replyingToId, setReplyingToId] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  const agencyUsersRef = useRef([]);
  const userRef = useRef(user);

  useEffect(() => {
    agencyUsersRef.current = agencyUsers;
  }, [agencyUsers]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const videoTitleFromSlug = useCallback(
    (slug) => videoBySlug.get(slug)?.title ?? slug.replace(/-/g, ' '),
    [videoBySlug],
  );

  const totalCommentCount = useMemo(
    () => comments.reduce((sum, c) => sum + 1 + (c.replies?.length || 0), 0),
    [comments],
  );

  const filteredComments = useMemo(() => {
    if (videoFilter === 'all') return comments;
    return comments.filter((c) => c.video_id === videoFilter);
  }, [comments, videoFilter]);

  const videoFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All Videos' },
      ...bootCampVideos.map((video) => ({
        value: videoSlugFromUrl(video.url),
        label: video.title,
      })),
    ],
    [],
  );

  useEffect(() => {
    if (!user?.agency_id) {
      setLoadingComments(false);
      return;
    }
    setLoadingComments(true);
    api.bootcampComments
      .getAll(user.agency_id)
      .then(setComments)
      .catch(console.error)
      .finally(() => setLoadingComments(false));
  }, [user?.agency_id]);

  useEffect(() => {
    if (!supabase || !user?.agency_id) return undefined;

    const channel = supabase
      .channel('bootcamp-comments-live')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bootcamp_comments',
          filter: `agency_id=eq.${user.agency_id}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row || row.deleted_at) return;
          if (row.user_id === userRef.current?.id) return;
          setComments((prev) => appendRealtimeComment(prev, row, agencyUsersRef.current));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.agency_id]);

  const handleToggleReaction = useCallback(
    async (commentId, emoji) => {
      if (!user?.agency_id || !user?.id) return;

      setComments((prev) =>
        updateCommentTree(prev, commentId, (c) => {
          const reactions = [...(c.reactions || [])];
          const idx = reactions.findIndex((r) => r.user_id === user.id && r.emoji === emoji);
          if (idx >= 0) {
            reactions.splice(idx, 1);
          } else {
            reactions.push({
              id: `optimistic-${Date.now()}`,
              user_id: user.id,
              emoji,
            });
          }
          return { ...c, reactions };
        }),
      );

      try {
        await api.bootcampComments.toggleReaction(user.agency_id, commentId, user.id, emoji);
      } catch (err) {
        console.error(err);
        const data = await api.bootcampComments.getAll(user.agency_id);
        setComments(data);
      }
    },
    [user?.agency_id, user?.id],
  );

  const handleDelete = useCallback(async (commentId) => {
    setComments((prev) => removeCommentFromTree(prev, commentId));
    try {
      await api.bootcampComments.delete(commentId);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleCreate = useCallback(
    async (videoId, body, parentId = null) => {
      if (!user?.agency_id || !user?.id || !body.trim() || !videoId) return;
      setSubmitting(true);
      try {
        const mentionedUserIds = extractMentionedUserIds(body, agencyUsers);
        const created = await api.bootcampComments.create(
          user.agency_id,
          user.id,
          videoId,
          body.trim(),
          mentionedUserIds,
          parentId,
        );
        if (parentId) {
          setComments((prev) =>
            prev.map((c) =>
              c.id === parentId ? { ...c, replies: [...(c.replies || []), created] } : c,
            ),
          );
        } else {
          setComments((prev) => [...prev, created]);
        }
        return true;
      } catch (err) {
        console.error(err);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [agencyUsers, user?.agency_id, user?.id],
  );

  const handleSubmitTopLevel = async () => {
    if (!activeVideoSlug || !composeBody.trim()) return;
    const ok = await handleCreate(activeVideoSlug, composeBody);
    if (ok) setComposeBody('');
  };

  const handleSubmitReply = async (parentComment) => {
    if (!replyDraft.trim()) return;
    const ok = await handleCreate(parentComment.video_id, replyDraft, parentComment.id);
    if (ok) {
      setExpandedReplies((prev) => new Set(prev).add(parentComment.id));
      setReplyDraft('');
      setReplyingToId(null);
    }
  };

  const toggleRepliesExpanded = useCallback((commentId) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  }, []);

  return (
    <div className="mt-6 pt-6 border-t border-gray-700">
      <Card className="overflow-hidden border-l-4 border-l-[#00AAFF] px-4 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-[#00AAFF]" />
            <h3 className="text-lg font-semibold text-gray-900">Discussion</h3>
            <span className="inline-flex items-center rounded-full bg-[#00AAFF]/10 px-2.5 py-0.5 text-xs font-semibold text-[#00AAFF]">
              {totalCommentCount}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Filter by video:</span>
            <div className="w-44 sm:w-52">
              <Select
                options={videoFilterOptions}
                value={videoFilter}
                onChange={(e) => setVideoFilter(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="py-4 border-b border-gray-100">
          <MentionTextarea
            value={composeBody}
            onChange={setComposeBody}
            onSubmit={handleSubmitTopLevel}
            placeholder="Write a comment... use @ to mention someone"
            agencyUsers={agencyUsers}
            disabled={submitting || !activeVideoSlug}
            composeMode
            videoTitle={activeVideo?.title}
          />
        </div>

        <div className="flex flex-col gap-4 pt-4 min-h-[120px]">
          {loadingComments ? (
            <p className="text-sm text-gray-400 text-center py-6">Loading comments…</p>
          ) : filteredComments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              No comments yet. Be the first to start the discussion!
            </p>
          ) : (
            filteredComments.map((comment) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                user={user}
                activeVideoSlug={activeVideoSlug}
                videoTitleFromSlug={videoTitleFromSlug}
                onLoadVideo={onLoadVideo}
                onReply={setReplyingToId}
                onDelete={handleDelete}
                onToggleReaction={handleToggleReaction}
                replyingToId={replyingToId}
                onCancelReply={() => {
                  setReplyingToId(null);
                  setReplyDraft('');
                }}
                onSubmitReply={handleSubmitReply}
                agencyUsers={agencyUsers}
                replyDraft={replyDraft}
                onReplyDraftChange={setReplyDraft}
                submitting={submitting}
                expandedReplies={expandedReplies}
                onToggleReplies={toggleRepliesExpanded}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

export default function BootCampTab() {
  const [activeUrl, setActiveUrl] = useState(() => bootCampVideos[0]?.url ?? null);
  const [watched, setWatched] = useState(loadProgress);

  useEffect(() => {
    try {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(watched));
    } catch {
      /* ignore storage errors */
    }
  }, [watched]);

  const activeVideo = useMemo(
    () => bootCampVideos.find((v) => v.url === activeUrl) ?? null,
    [activeUrl],
  );

  const activeVideoSlug = useMemo(
    () => (activeVideo ? videoSlugFromUrl(activeVideo.url) : null),
    [activeVideo],
  );

  const videoBySlug = useMemo(() => {
    const map = new Map();
    for (const v of bootCampVideos) {
      map.set(videoSlugFromUrl(v.url), v);
    }
    return map;
  }, []);

  const toggleWatched = useCallback((url) => {
    setWatched((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));
  }, []);

  const loadVideoBySlug = useCallback(
    (slug) => {
      const video = videoBySlug.get(slug);
      if (video) setActiveUrl(video.url);
    },
    [videoBySlug],
  );

  const watchedCount = watched.filter((url) => bootCampVideos.some((v) => v.url === url)).length;

  if (bootCampVideoCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <SafeIcon icon={FiVideo} className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">No videos yet</h3>
        <p className="text-sm text-gray-500 max-w-xs">
          Boot Camp training videos will appear here once they have been added.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">
          New agent training — work through each chapter in order.
        </p>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-primary">
          <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />
          {watchedCount} / {bootCampVideoCount} complete
        </span>
      </div>

      <div className="grid w-full grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Player */}
        <div className="lg:col-span-2 space-y-3">
          <Card className="overflow-hidden">
            <div className="bg-black aspect-video">
              {activeVideo && (
                <iframe
                  key={activeVideo.url}
                  src={activeVideo.url}
                  title={activeVideo.title}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              )}
            </div>
          </Card>
          {activeVideo && (
            <div className="px-1 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  {bootCampChapters.find((c) => c.chapter === activeVideo.chapter)?.title}
                  {' · '}Video {activeVideo.order}
                </p>
                <h2 className="text-lg font-bold text-gray-900">{activeVideo.title}</h2>
              </div>
              <button
                type="button"
                onClick={() => toggleWatched(activeVideo.url)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  watched.includes(activeVideo.url)
                    ? 'bg-green-50 text-green-700 hover:bg-green-100'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                <SafeIcon
                  icon={watched.includes(activeVideo.url) ? FiRotateCcw : FiCheckCircle}
                  className="w-4 h-4"
                />
                {watched.includes(activeVideo.url) ? 'Mark incomplete' : 'Mark complete'}
              </button>
            </div>
          )}

          <BootCampDiscussion
            activeVideo={activeVideo}
            activeVideoSlug={activeVideoSlug}
            videoBySlug={videoBySlug}
            onLoadVideo={loadVideoBySlug}
          />
        </div>

        {/* Chapter / lesson list */}
        <div className="space-y-4">
          {bootCampChapters.map((chapter) => (
            <Card key={chapter.chapter} className="overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-sm font-bold text-gray-900">{chapter.title}</p>
              </div>
              <ul className="divide-y divide-gray-100">
                {chapter.videos.map((video) => {
                  const isActive = video.url === activeUrl;
                  const isWatched = watched.includes(video.url);
                  return (
                    <li key={video.url}>
                      <button
                        type="button"
                        onClick={() => setActiveUrl(video.url)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                          isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <SafeIcon
                          icon={isWatched ? FiCheckCircle : isActive ? FiPlayCircle : FiCircle}
                          className={`mt-0.5 w-5 h-5 shrink-0 ${
                            isWatched ? 'text-green-500' : isActive ? 'text-primary' : 'text-gray-300'
                          }`}
                        />
                        <span className="min-w-0">
                          <span
                            className={`block text-sm font-medium truncate ${
                              isActive ? 'text-primary' : 'text-gray-900'
                            }`}
                          >
                            {video.title}
                          </span>
                          <span className="block text-xs text-gray-400">Video {video.order}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
